import Fuse from 'fuse.js'

import { plugin as $date } from '../../plugins/date'

import Model, { Request } from '../model'
import APIObject from '../object'
import Enum from '../enums'
import { LocalFilterController, Filter, RemoteFilter, FilterCategory, FilterOptions } from '../filter'

import Customer from './customer'
import CustomField from './customField'
import Employee, { EmployeeEmployeeType } from './employee'
import OrderItem, { ItemPaymentType } from './orderItem'
import Tax from './tax'

export default class Order extends Model {
  static modelName () {
    return 'order'
  }

  objectID () {
    return this.idOrder
  }

  static modelApiVersion () {
    return 2
  }

  relations () {
    return {
      customer: { type: Customer },
      customFields: { type: CustomField },
      employee: { type: Employee },
      employeeTips: { type: OrderEmployeeTip },
      data: { type: OrderData },
      tipType: { type: OrderTipType },
    }
  }

  static filter (filters, { from, to, format }) {
    const url = this.modelBaseURL() + '/filter'
    const request = {
      filters,
      from,
      to,
      format,
    }

    return this.requestItem(Request.post(url, JSON.stringify(request)), OrderFilterResponse)
  }

  match (query) {
    let match = false
    match = match || this.orderNumber.toLowerCase().includes(query)

    return match
  }

  static create ({ customer, notes, items = [] }) {
    const data = {
      idCustomer: customer.idCustomer,
      notes,
      items: items
        .map((item) => {
          return {
            type: item.constructor.modelName(),
            id: item.objectID(),
          }
        }),
    }

    const order = new Order(data)

    return order.save()
  }

  static recentCharges () {
    const url = this.modelBaseURL() + '/recentCharges'
    return this.requestList(Request.get(url), OrderItem)
  }

  static listMonth (year, month) {
    const url = this.modelBaseURL() + '/month?year=' + year + '&month=' + month
    return this.requestItem(Request.get(url), OrderListMonth)
  }

  /* listIDs (ids) {
    const data = ids.join(',')
    const url = this.constructor.modelBaseURL() + '/ids?ids=' + data
    return this.constructor.requestList(Request.get(url), this.constructor)
  } */

  static ids (ids, page = 1) {
    const url = this.modelBaseURL() + '/ids?page=' + page

    const data = {
      ids,
    }

    return this.requestList(Request.post(url, JSON.stringify(data)), this)
  }

  static listIDs (ids) {
    // use a default runner (to call the list method)
    const that = this

    const runner = (page) => {
      return that.ids(ids, page)
    }

    return this.listRunner(runner)
  }

  email () {
    const url = this.constructor.modelBaseURL() + '/email?id=' + this.objectID()
    return this.constructor.requestSuccess(Request.get(url))
  }

  open () {
    const url = this.constructor.modelBaseURL() + '/open?id=' + this.objectID()
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  close (email) {
    const sendEmail = email ? 1 : 0
    const url = this.constructor.modelBaseURL() + '/close?id=' + this.objectID() + '&email=' + sendEmail
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addProduct (product, quantity, employee) {
    const employeeId = employee ? employee.idEmployee : ''
    const url = this.constructor.modelBaseURL() + '/addProduct?id=' + this.objectID() + '&product=' + product.idProduct + '&quantity=' + quantity + '&employee=' + employeeId
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addCustomerCredit (price, employee) {
    const employeeId = employee ? employee.idEmployee : ''
    const url = this.constructor.modelBaseURL() + '/addCustomerCredit?id=' + this.objectID() + '&price=' + price + '&employee=' + employeeId
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addProducts (quantities, employee) {
    let url = this.constructor.modelBaseURL() + '/addProducts?id=' + this.objectID()

    if (employee?.idEmployee) {
      url += '&employee=' + employee.idEmployee
    }

    const data = {
      quantities: quantities
        .map(({ product, quantity }) => {
          return {
            product: product.idProduct,
            quantity,
          }
        }),
    }

    return this.constructor.requestItem(Request.post(url, data), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addProductWithBarcode (barcode) {
    const url = this.constructor.modelBaseURL() + '/addProductwithBarcode?id=' + this.objectID()
    return this.constructor.requestItem(Request.post(url, { barcode }), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addService (service, employee) {
    const employeeId = employee && employee.idEmployee ? employee.idEmployee : ''
    const url = this.constructor.modelBaseURL() + '/addService?id=' + this.objectID() + '&service=' + service.idService + '&employee=' + employeeId
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addReservation (reservation) {
    const url = this.constructor.modelBaseURL() + '/addReservation?id=' + this.objectID() + '&reservation=' + reservation.idReservation
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addReservations (reservations) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID()  + '/addReservations'

    const data = {
      reservations: [],
      reservationCustomers: [],
    }

    for (const reservation of reservations) {
      if (reservation.isSingle) {
        data.reservations.push(reservation.idReservation)
      }

      if (reservation.isGroup) {
        data.reservationCustomers.push(reservation.reservationCustomer.idReservationCustomer)
      }
    }

    return this.constructor.requestItem(Request.post(url, data), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  // NOTE: There were 3 addReservation functions - I renamed the second 2 "addReservationEmployee" & "addReservationCustomer"
  addReservationEmployee (reservation, employee) {
    const employeeId = employee ? employee.idEmployee : ''
    const url = this.constructor.modelBaseURL() + '/addReservation?id=' + this.objectID() + '&reservation=' + reservation.idReservation + '&employee=' + employeeId
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addReservationCustomer (reservationCustomer) {
    const url = this.constructor.modelBaseURL() + '/addReservationCustomer?id=' + this.objectID() + '&reservationCustomer=' + reservationCustomer.idReservationCustomer
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addReservationCustomers (reservationCustomers) {
    const url = this.constructor.modelBaseURL() + '/addReservationCustomers?id=' + this.objectID()

    const data = {
      reservationCustomers: reservationCustomers
        .map(({ idReservationCustomer }) => idReservationCustomer),
    }

    return this.constructor.requestItem(Request.post(url, data), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addGiftCard (giftCard) {
    const url = this.constructor.modelBaseURL() + '/addGiftCard?id=' + this.objectID() + '&giftCard=' + giftCard.idGiftCard
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addGiftCards (giftCards) {
    const url = this.constructor.modelBaseURL() + '/addGiftCards?id=' + this.objectID()

    const data = {
      giftCards: giftCards
        .map(({ idGiftCard }) => idGiftCard),
    }

    return this.constructor.requestItem(Request.post(url, data), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addCustomerPasses (customerPasses) {
    const url = this.constructor.modelBaseURL() + '/addCustomerPasses?id=' + this.objectID()

    const data = {
      customerPasses: customerPasses
        .map(({ idCustomerPass }) => idCustomerPass),
    }

    return this.constructor.requestItem(Request.post(url, data), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addCharge (charge) {
    const url = this.constructor.modelBaseURL() + '/addCharge?id=' + this.objectID()

    try {
      const data = charge.toJSON()
      return this.constructor.requestItem(Request.post(url, data), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
    } catch (error) {
      return error
    }
  }

  addDiscount (discount) {
    const url = this.constructor.modelBaseURL() + '/addDiscount?id=' + this.objectID()

    try {
      const data = discount.toJSON()
      return this.constructor.requestItem(Request.post(url, data), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
    } catch (error) {
      return error
    }
  }

  addPayment (payment) {
    const url = this.constructor.modelBaseURL() + '/addPayment?id=' + this.objectID()
    // return this.constructor.requestItem(Request.post(url, payment), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))

    try {
      const data = payment.toJSON()
      return this.constructor.requestItem(Request.post(url, data), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
    } catch (error) {
      return error
    }
  }

  addPaymentSavedCreditCard (amount, creditCard, idempotency_key) {
    const url = this.constructor.modelBaseURL() + '/addPaymentSavedCreditCard?id=' + this.objectID() + '&amount=' + amount + '&idCustomerCreditCard=' + creditCard.idCustomerCreditCard + '&idempotency_key=' + idempotency_key
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addPaymentNewCreditCard (amount, processor, params, saveCard) {
    const url = this.constructor.modelBaseURL() + '/addPaymentSavedCreditCard?id=' + this.objectID()
    const data = {
      amount,
      processor,
      saveCard,
    }
    const jsonData = Object.assign(data, params)
    return this.constructor.requestItem(Request.post(url, jsonData), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  addPaymentTerminal (guid, amount, params = {}) {
    const url = this.constructor.modelBaseURL() + '/addPaymentTerminal?id=' + this.objectID()

    const data = {
      guid,
      amount,
      params,
    }

    return this.constructor.requestItem(Request.post(url, JSON.stringify(data)), OrderTerminalPayment)
  }

  checkPaymentTerminal (guid, payment_id) {
    const url = this.constructor.modelBaseURL() + '/checkPaymentTerminal?id=' + this.objectID()

    const data = {
      guid,
      payment_id,
    }

    return this.constructor.requestItem(Request.post(url, JSON.stringify(data)), OrderTerminalPayment)
  }

  cancelPaymentTerminal (guid, payment_id) {
    const url = this.constructor.modelBaseURL() + '/cancelPaymentTerminal?id=' + this.objectID()

    const data = {
      guid,
      payment_id,
    }

    return this.constructor.requestItem(Request.post(url, JSON.stringify(data)), OrderTerminalPayment)
  }

  saveItem (item) {
    const url = this.constructor.modelBaseURL() + '/saveItem?id=' + this.objectID()
    return this.constructor.requestItem(Request.post(url, item), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  deleteItem (item) {
    const url = this.constructor.modelBaseURL() + '/deleteItem?id=' + this.objectID() + '&item=' + item.idOrderItem
    return this.constructor.requestItem(Request.get(url), Order).then(this.updateSelf(res => this.updateSelf(res)))
  }

  refundPayment (orderItem, amount, processorRefund, customerPayment) {
    const url = this.constructor.modelBaseURL() + '/refundPayment'

    if (customerPayment) {
      delete customerPayment.idLocationCustomerPayment
    }

    const jsonData = {
      order: this.objectID(),
      orderItem: orderItem.objectID(),
      amount,
      processorRefund,
      customerPayment,
    }
    return this.constructor.requestItem(Request.post(url, jsonData), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  requestPayment (amount, details) {
    const url = this.constructor.modelBaseURL() + '/requestPayment'
    const jsonData = {
      order: this.objectID(),
      amount,
      details,
    }

    return this.constructor.requestSuccess(Request.post(url, jsonData))
  }

  setTipSingle (amount) {
    const url = this.constructor.modelBaseURL() + '/setTip?id=' + this.objectID()
    const jsonData = {
      tipType: OrderTipType.single.value,
      amount,
    }
    return this.constructor.requestItem(Request.post(url, jsonData), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  setTipEmployee (employeeTips) {
    const url = this.constructor.modelBaseURL() + '/setTip?id=' + this.objectID()
    const tips = []

    for (const employeeTip of employeeTips) {
      tips.push({
        employee: employeeTip.employee.idEmployee,
        amount: employeeTip.amount,
      })
    }

    const jsonData = {
      tipType: OrderTipType.perEmployee.value,
      employeeTips: tips,
    }

    return this.constructor.requestItem(Request.post(url, jsonData), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  get hasServices () {
    return !!this.data.items.reservations.length
  }

  get hasProducts () {
    return !!this.data.items.products.length
  }

  get hasCharges () {
    return !!this.data.items.charges.length
  }

  get hasDiscounts () {
    return !!this.data.items.discounts.length
  }

  get hasPayments () {
    return !!this.data.items.payments.length
  }

  get hasItems () {
    return this.hasReservations || this.hasProducts || this.hasCharges || this.hasDiscounts || this.hasPayments
  }

  get hasRemaining () {
    return this.data.totals.remainder > 0
  }

  get hasOverpayment () {
    return this.data.totals.total < this.data.totals.payments
  }

  get hasCustomerCredit () {
    return this.data.items.products.some(item => item.isCustomerCredit)
  }

  get hasTip () {
    return this.data.totals.tip > 0
  }

  get isTipTypeSingle () {
    return this.tipType === OrderTipType.single
  }

  get isTipTypePerEmployee () {
    return this.tipType === OrderTipType.perEmployee
  }
}

export class OrderData extends APIObject {
  relations () {
    return {
      items: { type: OrderDataItems },
      totals: { type: OrderDataTotals },
      taxtotals: { type: OrderDataTaxTotal },
      employeeTips: { type: OrderEmployeeTip },
    }
  }
}

export class OrderDataItems extends APIObject {
  relations () {
    return {
      reservations: { type: OrderItem },
      products: { type: OrderItem },
      charges: { type: OrderItem },
      discounts: { type: OrderItem },
      payments: { type: OrderItem },
    }
  }
}

export class OrderDataTaxTotal extends APIObject {
  relations () {
    return {
      tax: { type: Tax },
    }
  }
}

export class OrderDataTotals extends APIObject {
}

export class OrderListMonth extends APIObject {
  relations () {
    return {
      orders: { type: Order },
    }
  }
}

export class OrderEmployeeTip extends APIObject {
  relations () {
    return {
      employee: { type: Employee },
    }
  }
}

export const OrderTipType = new Enum({
  single: { value: 0 },
  perEmployee: { value: 1 },
})

export class OrderFilterResponse extends APIObject {

}

export class OrderTerminalPayment extends APIObject {
  relations () {
    return {
      order: { type: Order },
      status: { type: OrderTerminalPaymentStatus },
    }
  }
}

export const OrderTerminalPaymentStatus = new Enum({
  pending: { value: 'pending' },
  processing: { value: 'processing' },
  complete: { value: 'complete' },
  cancelled: { value: 'cancelled' },
  timeout: { value: 'timeout' },
})

export class OrderFilterController extends LocalFilterController {
  constructor (context) {
    super(Order, { context, custom: true })

    const { $store, $i18n } = context

    const constructLabel = (value) => {
      const labels = value.slice(0, 3).map(({ title }) => title).join($i18n.t('general.listSeparator'))
      if (value.length > 3) {
        return $i18n.t('general.andMore', { list: labels, count: value.length - 3 })
      } else {
        return labels
      }
    }

    this.search = new Filter({
      label: $i18n.t('general.search'),
      matches (orders) {
        const fuse = new Fuse(orders, {
          keys: [
            'customer.firstName',
            'customer.lastName',
            'customer.company',
            'priceLabel',
            'orderNumber',
          ],
        })

        const results = fuse.search(this.value)
        return results.map(({ item }) => item)
      },
    })

    this.dates = new RemoteFilter('date', {
      label: $i18n.t('general.dates'),
      value: {
        start: null,
        finish: null,
      },
      component: 'OrderDateRange',
      force: true,
      validation () {
        const { start, finish } = this.value
        const valid = start && finish

        if (!valid) {
          return {
            status: false,
            message: $i18n.t('order.filters.invalidDate'),
          }
        }

        return null
      },
      valueLabel () {
        /* if (this.value.start && this.value.finish) {
          return $date.format(this.value.start * 1000, $date.presets.short) + '-' + $date.format(this.value.finish * 1000, $date.presets.short)
        }

        if (this.value.start) {
          return $date.format(this.value.start * 1000, $date.presets.short)
        }

        if (this.value.finish) {
          return $date.format(this.value.finish * 1000, $date.presets.short)
        } */
        return false
      },
      getParams () {
        return {
          from: this.value.start,
          to: this.value.finish,
          format: 'YYYYMMDD',
        }
      },
      clear () {
        this.value.start = null
        this.value.finish = null
      },
      isEmpty () {
        return !this.value.start && !this.value.finish
      },
    })

    this.paymentStatus = new RemoteFilter('status', {
      component: 'SegmentField',
      label: $i18n.t('order.filters.paymentStatus'),
      options: [
        { label: $i18n.t('order.filters.paidInFull'), value: 'paid' },
        { label: $i18n.t('order.unpaid'), value: 'unpaid' },
        { label: $i18n.t('order.partial'), value: 'partial' },
      ],
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      matches (orders) {
        return orders.filter((order) => {
          return this.value.includes(order.statusLabel.toLowerCase())
        })
      },
      valueLabel () {
        switch (this.value) {
          case 'paid':
            return $i18n.t('order.filters.paidInFull')
          case 'unpaid':
            return $i18n.t('order.unpaid')
          case 'partial':
            return $i18n.t('order.partial')
          default:
            return $i18n.t('general.all')
        }
      },
      getParams () {
        return {
          status: this.value,
        }
      },
    })


    this.saleStatus = new Filter({
      component: 'SegmentField',
      label: $i18n.t('order.saleStatus'),
      options: [
        { label: $i18n.t('general.open'), value: 'true' },
        { label: $i18n.t('general.closed'), value: 'false' },
      ],
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      matches (orders) {
        const value = this.value === 'true'

        return orders.filter((order) => {
          return order.closed === !value
        })
      },
      valueLabel () {
        return (this.value === 'true') ? $i18n.t('general.yes') : $i18n.t('general.no')
      },
    })

    this.employees = new RemoteFilter('employees', {
      label: $i18n.t('general.employees'),
      info: $i18n.t('order.filters.employees'),
      component: 'CheckboxGroupField',
      props: {
        search: true,
        class: 'border rounded overflow-hidden bg-gray-100 dark:bg-gray-900 h-80 dark:border-gray-800 px-1',
      },
      value: [],
      options: [],
      valueLabel () {
        const value = this.value

        if (value.length) {
          return constructLabel(value)
        }

        return $i18n.t('employee.all')
      },
      getParams () {
        return {
          employees: this.value.map(({ idEmployee }) => idEmployee),
        }
      },
      clear () {
        this.value = []
      },
    })

    this.services = new RemoteFilter('services', {
      label: $i18n.t('general.services'),
      info: $i18n.t('order.filters.services'),
      component: 'CheckboxCategoriesField',
      props: {
        search: true,
        class: 'border rounded overflow-hidden bg-gray-100 dark:bg-gray-900 h-80 dark:border-gray-800 px-1',
      },
      value: [],
      options: [],
      valueLabel () {
        const value = this.value

        if (value.length) {
          return constructLabel(value)
        }

        return $i18n.t('order.filters.allServices')
      },
      getParams () {
        return {
          services: this.value.map(({ idService }) => idService),
        }
      },
      clear () {
        this.value = []
      },
    })

    this.products = new RemoteFilter('products', {
      label: $i18n.t('general.products'),
      info: $i18n.t('order.filters.products'),
      component: 'CheckboxCategoriesField',
      props: {
        search: true,
        selectCategory: true,
        class: 'border rounded overflow-hidden bg-gray-100 dark:bg-gray-900 h-80 dark:border-gray-800 px-1',
      },
      value: [],
      options: [],
      valueLabel () {
        const value = this.value

        if (value.length) {
          return constructLabel(value)
        }

        return $i18n.t('product.all')
      },
      getParams () {
        return {
          products: this.value.map(({ idProduct }) => idProduct),
        }
      },
      clear () {
        this.value = []
      },
    })

    this.discounts = new RemoteFilter('discount', {
      component: 'SelectField',
      label: $i18n.t('general.discount'),
      options: [],
      valueLabel () {
        return this.value?.title
      },
      getParams () {
        return {
          discount: this.value?.idDiscountModel,
        }
      },
    })

    this.vouchers = new RemoteFilter('voucher', {
      component: 'SelectField',
      label: $i18n.t('general.promoCode'),
      options: [],
      features: ['vouchers'],
      valueLabel () {
        return `${this.value?.name} (${this.value?.code})`
      },
      getParams () {
        return {
          voucher: this.value?.idVoucher,
        }
      },
    })

    this.paymentType = new RemoteFilter('paymentType', {
      component: 'SelectField',
      label: $i18n.t('order.filters.paymentType'),
      options: Object.values(ItemPaymentType)
        .map((item) => {
          return { label: $i18n.t(item.description), value: item }
        }),
      props: {
        outline: true, //, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2'
      },
      valueLabel () {
        return this.value?.descrption
      },
      getParams () {
        return {
          paymentType: this.value?.value,
        }
      },
    })

    this.employeeOptions = new FilterOptions({
      map () {
        return $store
          ? Object.values($store.getters['employee/all'])
            .filter(({ type }) => type === EmployeeEmployeeType.employee)
            .map(employee => ({
              value: employee,
              text: employee.displayName,
              info: employee.type ? $i18n.t(employee.type.description) : $i18n.t('general.employee'),
              image: employee.images ? employee.images.square : null,
            }))
          : []
      },
      set: (options) => {
        this.employees.options = [{ text: $i18n.t('general.none'), value: '-1' }, ...options]
      },
    })

    this.serviceOptions = new FilterOptions({
      map () {
        return $store
          ? Object.values($store.getters['service/categorised']).map(category => ({
            label: category.name || '',
            options: category.services?.map(service => ({
              value: service,
              text: service.title,
            })),
          }))
          : []
      },
      set: (options) => {
        this.services.options = options
      },
    })

    this.productOptions = new FilterOptions({
      map () {
        const products = $store ? $store.getters['product/all'] : null
        return products
          ? Object.values(products).map(category => ({
            label: category.category ? category.category.name : $i18n.t('general.other'),
            options: category.products.map(product => ({
              value: product,
              text: product.name,
            })),
          }))
          : []
      },
      set: (options) => {
        this.products.options = options
      },
    })

    this.discountOptions = new FilterOptions({
      map () {
        return $store
          ? Object.values($store.getters['discountModel/all'])
            .map(discountModel => ({
              value: discountModel,
              label: discountModel.title,
            }))
          : []
      },
      set: (options) => {
        this.discounts.options = options
      },
    })

    this.voucherOptions = new FilterOptions({
      map () {
        return $store
          ? Object.values($store.getters['voucher/all'])
            .map(voucher => ({
              value: voucher,
              label: `${voucher.name} (${voucher.code})`,
            }))
          : []
      },
      set: (options) => {
        this.vouchers.options = options
      },
      features: ['vouchers'],
    })

    this.categorised = [
      new FilterCategory({
        label: $i18n.t('general.payments'),
        filters: [this.paymentStatus, this.paymentType, this.discounts, this.vouchers],
      }),
      new FilterCategory({
        label: $i18n.t('order.saleStatus'),
        filters: [this.saleStatus],
      }),
      new FilterCategory({
        label: $i18n.t('general.employees'),
        filters: [this.employees],
      }),
      new FilterCategory({
        label: $i18n.t('general.services'),
        filters: [this.services],
      }),
      new FilterCategory({
        label: $i18n.t('general.products'),
        filters: [this.products],
      }),
      new FilterCategory({
        label: $i18n.t('general.customFields'),
        filters: this.custom,
        features: ['custom-fields'],
      }),
    ]
  }

  get params () {
    const value = this.dates.value
    const from = value.start
    const to = value.finish

    return {
      from,
      to,
      format: 'YYYYMMDD',
    }
  }
}
